import * as yup from 'yup';

import { BankAccountType } from 'apis/legitimizer/types';

import { isValidBban, isValidBicOrRoutingCode, isValidIban } from '../bankValidations';
import { fieldOfIndustryOptions, legalFormOptions } from '../mappings';
import {
  BasicInfoFormValues,
  BeneficiariesFormValues,
  BankDetailsFormFieldsConfig,
  BasicInfoFormFieldsConfig,
  BeneficiaryDetailsFieldsConfig,
  BusinessRepresentativeFieldsConfig,
  DocumentsFormFieldsConfig,
  PEPDetailsFieldsConfig,
} from '../types';

const setRequiredValidation =
  (enforceValidation: boolean) =>
  (isFieldRequired: boolean) =>
  <T extends yup.BaseSchema>(validation: T, customRequiredValidation?: (validation: T) => T) => {
    const isRequired = enforceValidation && isFieldRequired;

    const validateRequired = (_validation: T) => {
      if (customRequiredValidation) {
        return customRequiredValidation(_validation);
      }

      return _validation.required({ id: 'validation.required' });
    };

    return isRequired ? validateRequired(validation) : validation;
  };

const bbanError = { message: { id: 'validation.bankAccountNumberError', values: { accountType: 'BBAN' } } };

const yupTestBban = (countryCode: string) => ({
  name: 'bban',
  test: (value: string | undefined, context: yup.TestContext) => {
    return !value || isValidBban(value, countryCode) || context.createError(bbanError);
  },
});

const bankIdErrorMessage = (countryCode: string) => {
  switch (countryCode) {
    case 'JPN':
      return { message: { id: 'validation.bankRoutingCodeError' } };
    default:
      return { message: { id: 'validation.bankBicInvalidError' } };
  }
};

const yupTestBankIdentifier = (countryCode: string) => ({
  name: 'bic',
  test: (value: string | undefined, context: yup.TestContext) => {
    return (
      !value || isValidBicOrRoutingCode(value, countryCode) || context.createError(bankIdErrorMessage(countryCode))
    );
  },
});

const yupTestpayoutRecipients = {
  name: 'payoutRecipients',
  message: { id: 'validation.emailListError' },
  test: (value: string | undefined) => {
    return yup
      .array()
      .of(yup.string().email())
      .min(1)
      .isValidSync(value?.split(',').map(value => value.trim()));
  },
};

const yupTestIban = {
  name: 'iban',
  message: { id: 'validation.bankAccountNumberError', values: { accountType: 'IBAN' } },
  test: (value: string | undefined) => {
    return !value || isValidIban(value);
  },
};

export const getBankDetailsSchema = (
  enforceValidation: boolean,
  fieldsConfig: BankDetailsFormFieldsConfig,
  countryCode: string,
  hasExistingDocument: boolean,
) => {
  const setRequiredField = setRequiredValidation(enforceValidation);
  const setDocumentRequiredField = setRequiredValidation(enforceValidation || hasExistingDocument);

  return yup.object({
    entityUUID: yup.string(),
    accountType: setRequiredField(fieldsConfig.accountType.required)(yup.string()),
    accountNumber: yup.string().when('accountType', (accountType: BankAccountType, schema: yup.BaseSchema) => {
      switch (accountType) {
        case 'IBAN':
          return setRequiredField(fieldsConfig.accountNumber.required)(schema.test(yupTestIban));
        case 'BBAN':
          return setRequiredField(fieldsConfig.accountNumber.required)(schema.test(yupTestBban(countryCode)));
        default:
          return schema.required();
      }
    }),
    accountOwner: setRequiredField(fieldsConfig.accountOwner.required)(yup.string().trim()),
    bankIdentifier: yup.string().when('accountType', (accountType: BankAccountType, schema: yup.BaseSchema) => {
      switch (accountType) {
        case 'IBAN':
          return setRequiredField(fieldsConfig.bankIdentifier.required)(
            schema.test(yupTestBankIdentifier(countryCode)),
          );
        // Bank identifier field is hidden when account type is BBAN, so it shouldn't be required
        case 'BBAN':
          return schema;
        default:
          return setRequiredField(fieldsConfig.bankIdentifier.required)(schema);
      }
    }),
    payoutRecipients: setRequiredField(fieldsConfig.payoutRecipients.required)(
      yup.string().test(yupTestpayoutRecipients),
    ),
    bankDocuments: yup
      .array()
      .of(
        yup.object({
          tempId: yup.string(),
          file: setDocumentRequiredField(fieldsConfig.bankDocuments.required)(yup.mixed<File>()),
        }),
      )
      .required(),
    nextSection: yup.string(),
  });
};

const yupLucidTest = {
  name: 'lucidId',
  message: { id: 'compliance.kyb.sections.basic-info.business-information.lucid-id.error' },
  test: (value: string | undefined) => {
    return !value || /^DE[0-9]{13}$/.test(value);
  },
};

export const getBasicInfoSchema = (
  enforceValidation: boolean,
  fieldsConfig: BasicInfoFormFieldsConfig,
): yup.SchemaOf<Omit<BasicInfoFormValues, 'businessRepresentatives'>> => {
  const setRequiredField = setRequiredValidation(enforceValidation);

  return yup.object({
    entityUUID: yup.string(),
    companyName: setRequiredField(fieldsConfig.companyName.required)(yup.string().trim()),
    streetAddress: setRequiredField(fieldsConfig.streetAddress.required)(yup.string().trim()),
    city: setRequiredField(fieldsConfig.city.required)(yup.string().trim()),
    postalCode: setRequiredField(fieldsConfig.postalCode.required)(yup.string().trim()),
    countryCode: setRequiredField(fieldsConfig.countryCode.required)(yup.string().trim()),
    registrationNumber: setRequiredField(fieldsConfig.registrationNumber.required)(yup.string()),
    isVatLiable: setRequiredField(fieldsConfig.isVatLiable.required)(yup.boolean()),
    vatId: setRequiredField(fieldsConfig.vatId.required)(
      enforceValidation
        ? yup.string().trim().when('isVatLiable', { is: true, then: yup.string().required() })
        : yup.string().trim(),
    ),
    tin: setRequiredField(fieldsConfig.tin.required)(yup.string().trim()),
    lucidId: setRequiredField(fieldsConfig.lucidId.required)(yup.string().trim().test(yupLucidTest)),
    legalForm: setRequiredField(fieldsConfig.legalForm.required)(
      yup
        .string()
        .trim()
        .test({
          name: 'legalForm',
          message: { id: 'validation.required' },
          test: (value: string | undefined) => !value || legalFormOptions.map(option => option.value).includes(value),
        }),
    ),
    dateOfRegistration: setRequiredField(fieldsConfig.dateOfRegistration.required)(yup.date()),
    yearOfRegistration: setRequiredField(fieldsConfig.yearOfRegistration.required)(yup.string().trim()),
    taxCountryCode: setRequiredField(fieldsConfig.taxCountryCode.required)(yup.string().trim()),
    fieldOfIndustry: setRequiredField(fieldsConfig.fieldOfIndustry.required)(
      yup
        .string()
        .trim()
        .test({
          name: 'fieldOfIndustry',
          message: { id: 'validation.required' },
          test: (value: string | undefined) =>
            !value || fieldOfIndustryOptions.map(option => option.value).includes(value),
        }),
    ),
    phone: yup.object({
      extension: setRequiredField(fieldsConfig.phone.required)(yup.string().trim()),
      number: setRequiredField(fieldsConfig.phone.required)(yup.string().trim()),
    }),
    nextSection: yup.string(),
  });
};

export const getBusinessRepresentativesSchema = (
  enforceValidation: boolean,
  fieldsConfig: BusinessRepresentativeFieldsConfig,
): yup.SchemaOf<Pick<BasicInfoFormValues, 'businessRepresentatives'>> => {
  const setRequiredField = setRequiredValidation(enforceValidation);
  return yup.object({
    businessRepresentatives: yup
      .array()
      .of(
        yup.object({
          entityUUID: yup.string(),
          name: setRequiredField(fieldsConfig.name.required)(yup.string().trim()),
          dateOfBirth: setRequiredField(fieldsConfig.dateOfBirth.required)(yup.date()),
          yearOfBirth: setRequiredField(fieldsConfig.yearOfBirth.required)(yup.string().trim()),
          placeOfBirth: setRequiredField(fieldsConfig.placeOfBirth.required)(yup.string().trim()),
          nationality: setRequiredField(fieldsConfig.nationality.required)(yup.string().trim()),
          nationalId: setRequiredField(fieldsConfig.nationalId.required)(yup.string().trim()),
          countryOfResidence: setRequiredField(fieldsConfig.countryOfResidence.required)(yup.string().trim()),
        }),
      )
      .required(),
  });
};

export const getDocumentsSchema = (
  enforceValidation: boolean,
  fieldsConfig: DocumentsFormFieldsConfig,
  hasExistingDocument: boolean,
) => {
  const setRequiredField = setRequiredValidation(enforceValidation || hasExistingDocument);

  return yup.object({
    registryExtractDocuments: yup
      .array()
      .of(
        yup.object({
          tempId: yup.string(),
          file: setRequiredField(fieldsConfig.registryExtractDocuments.required)(yup.mixed<File>()),
        }),
      )
      .required(),
  });
};

export const getBeneficiariesDetailsSchema = (
  enforceValidation: boolean,
  fieldsConfig: BeneficiaryDetailsFieldsConfig,
): yup.SchemaOf<Pick<BeneficiariesFormValues, 'beneficiaries'>> => {
  const setRequiredField = setRequiredValidation(enforceValidation);

  return yup.object({
    beneficiaries: yup.array().of(
      yup.object({
        entityUUID: yup.string(),
        name: setRequiredField(fieldsConfig.name.required)(yup.string().trim()),
        street: setRequiredField(fieldsConfig.street.required)(yup.string().trim()),
        postalCode: setRequiredField(fieldsConfig.postalCode.required)(yup.string().trim()),
        city: setRequiredField(fieldsConfig.city.required)(yup.string().trim()),
        country: setRequiredField(fieldsConfig.country.required)(yup.string().trim()),
        nationality: setRequiredField(fieldsConfig.nationality.required)(yup.string().trim()),
        nationalId: setRequiredField(fieldsConfig.nationalId.required)(yup.string().trim()),
        dateOfBirth: setRequiredField(fieldsConfig.dateOfBirth.required)(yup.date()),
        yearOfBirth: setRequiredField(fieldsConfig.yearOfBirth.required)(yup.string().trim()),
      }),
    ),
    nextSection: yup.string(),
  });
};

export const getPepsSchema = (
  enforceValidation: boolean,
  fieldsConfig: PEPDetailsFieldsConfig,
): yup.SchemaOf<Pick<BeneficiariesFormValues, 'peps'>> => {
  const setRequiredField = setRequiredValidation(enforceValidation);

  return yup.object({
    peps: yup.array().of(
      yup.object({
        entityUUID: yup.string(),
        name: setRequiredField(fieldsConfig.name.required)(yup.string().trim()),
        officialPosition: setRequiredField(fieldsConfig.officialPosition.required)(yup.string().trim()),
        country: setRequiredField(fieldsConfig.country.required)(yup.string().trim()),
        dateOfExpiration: setRequiredField(fieldsConfig.dateOfExpiration.required)(yup.date()),
        yearOfExpiration: setRequiredField(fieldsConfig.dateOfExpiration.required)(yup.string().trim()),
      }),
    ),
    nextSection: yup.string(),
  });
};
